<template>
  <ontos-base v-bind="baseProps" flush ref="base">
    <ontos-auth-layout :title="$tt('signup-title')" :subtitle="$tt('signup-subtitle')">
      <!-- O mesmo botão do login, e não um "cadastrar com o Google": pelo provedor não há dois
           caminhos — a conta nasce no primeiro clique. -->
      <template #social>
        <ontos-google-sign-in @signed="enter()" @failed="onGoogleFailed($event)"/>
      </template>

      <v-form @submit.prevent="signup()">
        <v-text-field v-model="name" :label="$tt('name')" autocomplete="name"
                      :error-messages="errorMessages.name"
                      @update:model-value="errorMessages.name = ''"/>

        <v-text-field v-model="email" :label="$tt('email')" type="email" autocomplete="email"
                      class="mt-2"
                      :error-messages="errorMessages.email"
                      @update:model-value="errorMessages.email = ''"/>

        <v-text-field v-model="password" :label="$tt('password')" type="password"
                      autocomplete="new-password" class="mt-2"
                      :error-messages="errorMessages.password"
                      @update:model-value="errorMessages.password = ''"/>

        <v-text-field v-model="confirmPassword" :label="$tt('password-confirm')" type="password"
                      autocomplete="new-password" class="mt-2"
                      :error-messages="errorMessages.confirmPassword"
                      @update:model-value="errorMessages.confirmPassword = ''"/>

        <v-btn type="submit" block color="primary" size="large" class="mt-6" :loading="isSaving">
          {{ $tt('signup') }}
        </v-btn>
      </v-form>

      <template #foot>
        <span>{{ $tt('have-account-message') }}</span>
        <router-link class="plain-link ms-1" :to="{name: 'login'}">{{ $tt('login') }}</router-link>
      </template>
    </ontos-auth-layout>

    <!-- A pergunta no chassi de sempre, em vez do `v-dialog` com `v-card` escrito à mão que
         havia aqui. Uma saída só: a conta foi criada, e o que resta é ir verificar o e-mail. -->
    <ontos-ask-dialog v-if="created"
                      :title="$tt('auth.account-created-title')"
                      :message="$tt('auth.account-created-message')"
                      :actions="createdActions"
                      @pick="toLogin()"
                      @closed="toLogin()"/>
  </ontos-base>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import RouteComponent from "@/components/shared/RouteComponent";
import AuthLayout from "@/components/home/AuthLayout.vue";
import GoogleSignIn from "@/components/home/GoogleSignIn.vue";
import AskDialog, {Ask, okAction} from "@/components/shared/AskDialog.vue";
import {AuthRefusal, handOverRefusal} from "@/utils/authRefusal";

@Component({
  components: {
    'ontos-auth-layout': AuthLayout,
    'ontos-google-sign-in': GoogleSignIn,
    'ontos-ask-dialog': AskDialog
  }
})
class Signup extends RouteComponent {
  name = ''
  email = ''
  password = ''
  confirmPassword = ''
  created = false

  errorMessages = {
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  }

  get createdActions(): Ask[] {
    return [okAction()]
  }

  async enter() {
    await this.$router.push({name: 'home'})
  }

  /**
   * A recusa do Google — e, quando é a do convite, ela não cabe nesta tela.
   *
   * Pelo provedor **a conta já nasceu** no clique: não há o que cadastrar de novo, e repetir o
   * botão repetiria a mesma recusa. Então vai para o login, com o endereço no campo, que é de
   * onde se tenta depois de a aprovação sair.
   *
   * A frase vem pronta do botão, que é o dono dela nas duas telas — esta só troca de tela.
   *
   * O resto é do provedor — popup bloqueado, conta que já tem senha — e se resolve aqui mesmo.
   */
  async onGoogleFailed(refusal: AuthRefusal) {
    if (!refusal.notApproved) {
      if (refusal.email) this.email = refusal.email
      this.errorMessages.email = refusal.message
      return
    }
    handOverRefusal(refusal)
    await this.$router.push({name: 'login'})
  }

  async toLogin() {
    this.created = false
    await this.$router.push({name: 'login'})
  }

  /** As duas senhas conferem? Vazio não é erro aqui — disso cuida o `required`. */
  private samePassword(): string {
    return this.password && this.confirmPassword && this.password !== this.confirmPassword ?
        this.$tt('validation.passwords-differ') as string : ''
  }

  async signup() {
    await this.saving(async () => {
      this.errorMessages.name = this.required(this.name)
      this.errorMessages.email = this.required(this.email)
      this.errorMessages.password = this.required(this.password)
      this.errorMessages.confirmPassword = this.required(this.confirmPassword) || this.samePassword()
      if (Object.values(this.errorMessages).some(message => !!message)) return

      try {
        await this.$auth.signup({name: this.name, email: this.email, password: this.password})
        this.created = true
      } catch (err: any) {
        this.report(err)
      }
    })
  }

  /** A recusa do Firebase no campo de que ela fala. Nunca o `err.message`: é texto em inglês. */
  private report(err: any) {
    switch (err?.code) {
      case 'auth/email-already-in-use':
        this.errorMessages.email = this.$tt('auth.email-in-use') as string; break
      case 'auth/weak-password':
        this.errorMessages.password = this.$tt('auth.weak-password') as string; break
      case 'auth/invalid-email':
        this.errorMessages.email = this.$tt('auth.invalid-email') as string; break
      default:
        if (String(err?.code || '').startsWith('auth/'))
          this.errorMessages.email = this.$tt('auth.generic-error') as string
        else throw err
    }
  }
}

export default toNative(Signup)
</script>
